<script setup lang="ts">
import type { Property, PropertyImage } from '~/core/entities/property';

import { EventBusKeys } from '~/core/helpers/symbols';
import { getPropertyImageAlt } from '~/utils';

import { StandardSlider } from '~/components/StandardSlider';
import { Skeleton } from '~/components/ui/skeleton';

defineOptions({
	name: 'PropertyPhotoCarouselMobileDeprecated'
});

interface CarouselItem {
	image: PropertyImage;
	index: number;
}

const props = withDefaults(
	defineProps<{
		property: Property | null;
		images: PropertyImage[];
		isLoading?: boolean;
		sectionName?: import('~/types/mixpanel').SectionName;
	}>(),
	{
		isLoading: false,
		sectionName: 'hero section'
	}
);

const emit = defineEmits<{
	'photo-clicked': [
		payload: {
			photoIndex: number;
			photoUrl: string;
			clickType: 'photo' | 'cta';
		}
	];
}>();

const standardSliderRef = useTemplateRef<{
	currentCarouselItemIndex: number;
	scrollableSlidesNumber: number;
	scrollTo: (index: number, immediate?: boolean) => void;
	reinitializeCarousel: (() => void) | undefined;
}>('standardSliderRef');

const carouselItems = computed<CarouselItem[]>(() =>
	props.images.map((image, index) => ({
		image,
		index
	}))
);

const visibleSlidesCount = computed(() =>
	Math.max(
		1,
		carouselItems.value.length -
			(standardSliderRef.value?.scrollableSlidesNumber ??
				carouselItems.value.length) +
			1
	)
);

const currentPhotoCount = computed(() => {
	const currentIndex = standardSliderRef.value?.currentCarouselItemIndex ?? 0;

	return Math.min(props.images.length, currentIndex + visibleSlidesCount.value);
});

function getImageAlt(index: number | null) {
	return getPropertyImageAlt(
		props.property?.propertyType?.name,
		props.property?.suites,
		props.property?.region,
		props.property?.city,
		props.property?.askingPrice,
		index
	);
}

function handlePhotoClick(
	photoIndex: number,
	photoUrl: string,
	clickType: 'photo' | 'cta' = 'photo'
) {
	emit('photo-clicked', {
		photoIndex,
		photoUrl,
		clickType
	});
}

const mobileCarouselCountBus = useEventBus(
	EventBusKeys.mobileHeroSectionPhotoCount
);

watch(
	currentPhotoCount,
	newValue => {
		mobileCarouselCountBus.emit(newValue);
	},
	{ immediate: true }
);
</script>

<template>
	<div class="size-full xl:hidden">
		<component
			:is="StandardSlider<CarouselItem>"
			ref="standardSliderRef"
			:items="carouselItems"
			:is-loading="isLoading"
			:section-name="sectionName"
			carousel-item-size="half-seamless"
			:slides-to-scroll="1"
			:carousel-options="{
				cyclic: true,
				align: 'center',
				class: 'size-full',
				carouselWrapperClasses: 'h-full'
			}"
			:show-navigation-arrows="true"
			:always-show-arrows="true"
			:display-title="false"
			:display-footer="false"
			:limit-photos="false"
			title=""
			container-class="!p-0 h-full"
			main-container-class="h-full"
			class="h-full"
			carousel-content-class="!p-0 h-full !gap-1"
		>
			<template #items="{ item, index }">
				<div class="h-full w-full overflow-hidden bg-black">
					<Skeleton :is-active="isLoading" class="size-full" variant="white">
						<NuxtImg
							:src="item.image.watermarkUrl"
							:alt="getImageAlt(index)"
							width="640"
							height="360"
							sizes="xs:375px sm:640px md:512px lg:512px"
							densities="x1"
							:preload="index === 0 ? { fetchPriority: 'high' } : undefined"
							:loading="index === 0 ? 'eager' : 'lazy'"
							decoding="async"
							class="aspect-video size-full object-cover"
							format="webp"
							quality="80"
							@click="handlePhotoClick(item.index, item.image.watermarkUrl)"
						/>
					</Skeleton>
				</div>
			</template>

			<template #skeleton>
				<div class="size-full">
					<Skeleton class="size-full" variant="white" :is-active="true" />
				</div>
			</template>
		</component>
	</div>
</template>
